import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { Document, PipelineIngestFile, IngestBatchResponse } from '../types/api';

export interface ListIngestFilters {
  q?: string | undefined;
  folder_path?: string | undefined;
  folder_prefix?: string | undefined;
  promoted?: 'true' | 'false' | 'all' | undefined;
  include_drafts?: 'true' | 'false' | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useIngestList(filters: ListIngestFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    enabled: !!projectId,
    queryKey: ['ingest', projectId, filters],
    queryFn: async () => {
      const query: Record<string, string | number | boolean | null | undefined> = { ...filters };
      const res = await api.get<{ files: PipelineIngestFile[] }>('/ingest/files', query);
      return res.files;
    },
  });
}

export function useIngestBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      files: Array<{
        client_id: string;
        filename: string;
        folder_path: string;
        content_type: string;
        size_bytes: number;
        sha256: string;
      }>;
    }) => api.post<IngestBatchResponse>('/ingest/batch', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingest'] }),
  });
}

export function useFinalizeIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api.post<{ file: PipelineIngestFile }>(`/ingest/files/${id}/finalize`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingest'] }),
  });
}

export function usePatchIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; display_name?: string | null; folder_path?: string }) => {
      const { id, ...body } = input;
      return api.patch<{ file: PipelineIngestFile }>(`/ingest/files/${id}`, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingest'] }),
  });
}

export function useDeleteIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete<void>(`/ingest/files/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingest'] }),
  });
}

export function usePromoteIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; title?: string | null; description?: string | null }) => {
      const { id, ...body } = input;
      return api.post<{ document: Document }>(`/ingest/files/${id}/promote`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingest'] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useBulkMoveIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; folder_path: string }) =>
      api.post<{ updated: number }>('/ingest/files/bulk-move', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingest'] }),
  });
}

export function useBulkRenameIngest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { items: Array<{ id: string; display_name: string }> }) =>
      api.post<{ updated: number }>('/ingest/files/bulk-rename', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ingest'] }),
  });
}

export function useIngestDownloadUrl() {
  return useMutation({
    mutationFn: async (id: string) =>
      api.get<{ url: string; expires_at: string; filename: string; content_type: string }>(
        `/ingest/files/${id}/download-url`,
      ),
  });
}
